import React, { useState, useEffect, useRef } from 'react';
import ImageEditor from './ImageEditor';
import PinAuth from './PinAuth';
import API_URL from './config';
import { buildPreviewFromTiff } from './buildPreviewFromTiff';
import KmlSetup from './kml/KmlSetup';
import SatelliteBackdrop from './kml/SatelliteBackdrop';
import CanvasResizer from './kml/CanvasResizer';
import MarkerAdder from './kml/MarkerAdder';
import { createFrame, createFrameFromBounds, autoResolution } from './kml/frame';

// Fallback path: send the JPG to the backend and let Pillow produce a
// thumbnail. Used when client-side TIFF decode fails (exotic TIFF variant,
// out-of-memory in the browser, etc.). Much slower and load-sensitive, so we
// surface a warning banner when this path is used.
const fetchServerThumbnail = async (file) => {
  const form = new FormData();
  form.append('image', file);
  const res = await fetch(`${API_URL}/thumbnail`, { method: 'POST', body: form });
  if (!res.ok) {
    throw new Error(`thumbnail request failed: ${res.status}`);
  }
  const origW = parseInt(res.headers.get('X-Original-Width') || '0', 10);
  const origH = parseInt(res.headers.get('X-Original-Height') || '0', 10);
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  return { objectUrl, origW, origH };
};

// Format seconds as "12s" or "2m 34s".
const fmtDuration = (sec) => {
  if (!isFinite(sec) || sec < 0) return '';
  const s = Math.round(sec);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${s % 60}s`;
};

// Landing choice between the two planning surfaces.
const ModeSelect = ({ onPick }) => (
  <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center', gap: 22, color: 'white',
                fontFamily: 'system-ui, Arial, sans-serif' }}>
    <h1 style={{ margin: 0, fontSize: 22, fontWeight: 600 }}>Path Planning GUI</h1>
    <p style={{ margin: 0, opacity: 0.6, fontSize: 13 }}>Choose what you are planning on.</p>
    <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', justifyContent: 'center' }}>
      {[
        { id: 'orthophoto', title: 'Orthophoto', icon: '🛩️',
          desc: 'Drone JPG + GeoTIFF pair. Highest detail; needs a flight.' },
        { id: 'satellite', title: 'Satellite / KML', icon: '🛰️',
          desc: 'Plan on satellite imagery. KML optional — or just enter a coordinate.' },
      ].map((m) => (
        <button key={m.id} onClick={() => onPick(m.id)} style={{
          width: 260, textAlign: 'left', background: '#1c1c1e', color: '#f2f2f7',
          border: '1px solid #3a3a3c', borderRadius: 12, padding: 20, cursor: 'pointer',
          fontFamily: 'inherit',
        }}>
          <div style={{ fontSize: 26 }}>{m.icon}</div>
          <div style={{ fontSize: 16, fontWeight: 600, margin: '8px 0 6px' }}>{m.title}</div>
          <div style={{ fontSize: 12, opacity: 0.65, lineHeight: 1.45 }}>{m.desc}</div>
        </button>
      ))}
    </div>
  </div>
);

const App = () => {
  const [uploadedImage, setUploadedImage] = useState("");
  const [tifFilename, setTifFilename] = useState("");
  const [tifFile, setTifFile] = useState(null);
  const [calculatedPath, setCalculatedPath] = useState(null);
  const [jpgImage, setJpgImage] = useState(null);
  const [originalImageSize, setOriginalImageSize] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  // Which planning surface the operator chose: null (not chosen yet),
  // 'orthophoto' (JPG + GeoTIFF) or 'satellite' (satellite imagery, KML optional).
  // Chosen EXPLICITLY rather than inferred from the uploaded file type, so
  // "plan on satellite with no files at all" is a first-class entry point.
  const [mode, setMode] = useState(null);
  const [satFrame, setSatFrame] = useState(null);
  const [satFeatures, setSatFeatures] = useState([]);
  const [satName, setSatName] = useState('satellite_field');
  // Kept so the canvas can be rebuilt at a new size after opening.
  const [satSetup, setSatSetup] = useState(null); // { bounds, center, margin, autoRes, manualCm }
  // Operator-placed reference points (lat/lon). Reference geometry only — these are NOT
  // navigation lines and never enter the lines/lineGroups export.
  const [satMarkers, setSatMarkers] = useState([]);
  const addSatMarker = React.useCallback((m) => setSatMarkers((prev) => [...prev, { ...m, id: `m${prev.length + 1}_${Math.round(m.lat * 1e6)}` }]), []);
  const removeSatMarker = React.useCallback((id) => setSatMarkers((prev) => prev.filter((x) => x.id !== id)), []);

  // Rebuild the frame at a new margin. ImageEditor sees a new `frame` prop and
  // remaps existing geometry through UTM, so nothing drawn is lost.
  const resizeSatelliteCanvas = React.useCallback((nextMargin) => {
    if (!satSetup) return;
    const { bounds, center, autoRes, manualCm } = satSetup;
    const probe = bounds
      ? createFrameFromBounds(bounds, { cmPerPixel: 1, marginMeters: nextMargin })
      : createFrame({ centerLon: center.lon, centerLat: center.lat,
                      widthMeters: nextMargin * 2, heightMeters: nextMargin * 2, cmPerPixel: 1 });
    const extent = Math.max(probe.width, probe.height) / 100; // cmPerPixel 1 => units are cm
    const cmPerPixel = autoRes ? autoResolution(extent) : manualCm;
    const next = bounds
      ? createFrameFromBounds(bounds, { cmPerPixel, marginMeters: nextMargin })
      : createFrame({ centerLon: center.lon, centerLat: center.lat,
                      widthMeters: nextMargin * 2, heightMeters: nextMargin * 2, cmPerPixel });
    setSatFrame(next);
    setSatSetup((s0) => ({ ...s0, margin: nextMargin }));
  }, [satSetup]);

  // Progress modal state for the big TIFF decode. progress is 0..1, or null
  // when no load is in flight. `stage` is a human-readable label; `warning`
  // shows a banner when we've fallen back to the slow server path.
  const [loadProgress, setLoadProgress] = useState(null); // { progress, stage, startedAt, warning? }

  // Live ticker for elapsed time — we re-render once per second while a load
  // is in flight so elapsed/ETA stay current without requiring the decoder
  // to emit progress events every second.
  const [nowTick, setNowTick] = useState(0);
  useEffect(() => {
    if (loadProgress === null) return undefined;
    const t = setInterval(() => setNowTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [loadProgress]);

  const correctPin = '02470';

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);

    const jpgFile = files.find(file => file.type === "image/jpeg");
    const tifFile = files.find(file => file.name.toLowerCase().endsWith('.tif') || file.name.toLowerCase().endsWith('.tiff'));

    if (!jpgFile || !tifFile) {
      alert("Please select both a JPG file and a TIF file.");
      return;
    }

    // Start the progress modal. The client-side path is the default; we only
    // fall back to the server on explicit failure, and the banner warns the
    // user when that happens (server path can take minutes).
    const startedAt = Date.now();
    setLoadProgress({ progress: 0, stage: 'Starting…', startedAt });

    try {
      const { objectUrl, origW, origH } = await buildPreviewFromTiff(tifFile, {
        maxDimension: 4096,
        onProgress: (progress, stage) => {
          setLoadProgress({ progress, stage, startedAt });
        },
      });

      setJpgImage(objectUrl);
      setOriginalImageSize({ width: origW, height: origH });
      setTifFilename(tifFile.name);
      setTifFile(tifFile);
      setLoadProgress(null);
    } catch (clientErr) {
      console.warn('Client-side TIFF preview failed, falling back to server:', clientErr);
      // Fall back to the server endpoint. Surface the slowness to the user
      // with a warning banner on the progress modal so minutes-long waits
      // are explained rather than mysterious.
      setLoadProgress({
        progress: 0,
        stage: 'Processing on server (this may take a few minutes)…',
        startedAt: Date.now(),
        warning: 'Client-side preview failed. Using server fallback — expect a longer wait.',
      });
      try {
        const { objectUrl, origW, origH } = await fetchServerThumbnail(jpgFile);
        setJpgImage(objectUrl);
        setOriginalImageSize(origW && origH ? { width: origW, height: origH } : null);
        setTifFilename(tifFile.name);
        setTifFile(tifFile);
      } catch (serverErr) {
        console.error('Server thumbnail also failed, using direct blob URL:', serverErr);
        setJpgImage(URL.createObjectURL(jpgFile));
        setOriginalImageSize(null);
        setTifFilename(tifFile.name);
        setTifFile(tifFile);
      } finally {
        setLoadProgress(null);
      }
    }
  };

  const handleCalculatePath = (path) => {
    setCalculatedPath(path);
  };

  // Derived values for the progress modal. Elapsed is always accurate.
  // ETA is a linear extrapolation from progress-so-far; it's deliberately
  // shown as a rough estimate ("~12s remaining") because early-phase
  // progress jumps aren't uniform.
  let elapsedSec = 0;
  let etaSec = null;
  if (loadProgress) {
    elapsedSec = (Date.now() - loadProgress.startedAt) / 1000;
    if (loadProgress.progress > 0.05 && loadProgress.progress < 1) {
      etaSec = (elapsedSec / loadProgress.progress) - elapsedSec;
    }
  }
  // Reference nowTick so React re-renders each second while loading.
  void nowTick;

  const hasImage = Boolean(jpgImage && tifFilename);

  return (
    <div className="App" style={{
      // Fullscreen shell. No padding / no green background — the image
      // editor fills the viewport and all chrome floats on top of it.
      position: 'relative',
      // 100% not 100vw: 100vw INCLUDES the scrollbar width, so with any vertical
      // scrollbar the shell renders wider than the content box and the page
      // scrolls sideways (the centred shell lands at a negative left offset).
      // Invisible on the image path, which centres its canvas; the KML view's
      // full-height sidebar made it obvious by clipping.
      width: '100%',
      height: '100vh',
      // App.css gives .App `padding: 20px`. Under the default content-box
      // sizing that padding is ADDED to the 100%/100vh, making the shell 40px
      // wider than the viewport and scrolling the page sideways. border-box
      // folds the padding in.
      boxSizing: 'border-box',
      backgroundColor: '#111', // neutral dark behind any empty areas while loading
      overflow: 'hidden',
    }}>
      {!isAuthenticated ? (
        // Auth screen keeps a centered layout — no image behind it yet.
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: '#CCFFCC',
        }}>
          <PinAuth correctPin={correctPin} onAuthenticate={() => setIsAuthenticated(true)} />
        </div>
      ) : !mode ? (
        <ModeSelect onPick={setMode} />
      ) : mode === 'satellite' ? (
        satFrame ? (
          // The SAME editor as the orthophoto path — same toolbars, modes,
          // dialogs, export. Only the frame and the backdrop differ.
          <>
          <ImageEditor
            frame={satFrame}
            backdrop={({ currentScale, position }) => (
              <SatelliteBackdrop frame={satFrame} features={satFeatures} markers={satMarkers}
                currentScale={currentScale} position={position} />
            )}
            originalImageSize={{ width: satFrame.width, height: satFrame.height }}
            tifname={satName}
            onCalculatePath={handleCalculatePath}
          />
          <MarkerAdder
            frame={satFrame}
            markers={satMarkers}
            onAdd={addSatMarker}
            onRemove={removeSatMarker}
          />
          {satSetup && (
            <CanvasResizer
              margin={satSetup.margin}
              onApply={resizeSatelliteCanvas}
              canvasW={satFrame.width * satFrame.mPerUnit}
              canvasH={satFrame.height * satFrame.mPerUnit}
              gsd={satFrame.gsd}
              geometryCount={satFeatures.length}
            />
          )}
          </>
        ) : (
          <KmlSetup
            onReady={({ frame, features, name, bounds, center, margin, autoRes, manualCm }) => {
              setSatFrame(frame);
              setSatFeatures(features);
              setSatName(name || 'satellite_field');
              setSatSetup({ bounds, center, margin, autoRes, manualCm });
            }}
            onBack={() => setMode(null)}
          />
        )
      ) : !hasImage ? (
        // No image yet: show a centered upload prompt on the dark shell.
        // Once an image is loaded we swap to the fullscreen editor. The
        // old behavior kept an inline "Upload JPG and TIF files:" row
        // above the canvas, but that ate vertical space from the map —
        // moved into a kebab entry in UtilityCluster (future).
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          gap: '16px',
          color: 'white',
          fontFamily: 'Arial, sans-serif',
        }}>
          <h1 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 600 }}>Path Planning GUI</h1>
          <label htmlFor="file-upload" style={{
            padding: '14px 22px',
            borderRadius: 10,
            backgroundColor: '#2e7d32',
            cursor: 'pointer',
            fontSize: '1rem',
          }}>
            Upload JPG + TIF files
          </label>
          <input
            id="file-upload"
            type="file"
            accept=".jpg,.jpeg,.tif,.tiff"
            onChange={handleFileUpload}
            multiple
            style={{ display: 'none' }}
          />
          <p style={{ fontSize: '0.8rem', opacity: 0.7, margin: 0 }}>
            Select one JPG and one TIF (orthomosaic) at the same time.
          </p>
        </div>
      ) : (
        // Image loaded: ImageEditor covers the entire viewport; all
        // toolbars/panels float as overlays on top of the image.
        <>
          <ImageEditor
            image={jpgImage}
            tifname={tifFilename}
            tifFile={tifFile}
            originalImageSize={originalImageSize}
            onCalculatePath={handleCalculatePath}
          />
        </>
      )}

      {/* Progress modal for the TIFF decode / server fallback. Rendered
          regardless of auth/image state so a TIFF load started from the
          upload-prompt screen can finish even though the app has swapped
          to the fullscreen editor mid-load. */}
      {loadProgress && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 5000,
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '1.5rem 2rem',
            minWidth: '22rem',
            maxWidth: '30rem',
            boxShadow: '0 10px 40px rgba(0,0,0,0.25)',
            fontFamily: 'Arial, sans-serif',
          }}>
            <h2 style={{ margin: '0 0 0.5rem 0', fontSize: '1.2rem' }}>Loading image</h2>
            <p style={{ margin: '0 0 1rem 0', color: '#555', fontSize: '0.95rem' }}>
              {loadProgress.stage}
            </p>

            {/* Determinate progress bar. */}
            <div style={{
              width: '100%',
              height: '14px',
              backgroundColor: '#e0e0e0',
              borderRadius: '7px',
              overflow: 'hidden',
              marginBottom: '0.5rem',
            }}>
              <div style={{
                width: `${Math.max(2, Math.round(loadProgress.progress * 100))}%`,
                height: '100%',
                backgroundColor: '#4CAF50',
                transition: 'width 0.2s ease',
              }} />
            </div>

            {/* Elapsed / ETA line. */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontSize: '0.85rem',
              color: '#666',
            }}>
              <span>{Math.round(loadProgress.progress * 100)}%</span>
              <span>
                Elapsed: {fmtDuration(elapsedSec)}
                {etaSec !== null && ` · ~${fmtDuration(etaSec)} remaining`}
              </span>
            </div>

            {loadProgress.warning && (
              <div style={{
                marginTop: '1rem',
                padding: '0.6rem 0.8rem',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffe69c',
                borderRadius: '6px',
                color: '#664d03',
                fontSize: '0.85rem',
              }}>
                ⚠ {loadProgress.warning}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default App;
